import React, { useState } from 'react';
import {
  Camera,
  CameraResultType,
  CameraSource,
} from '@capacitor/camera';

import ClinicalAudiogramChart from './ClinicalAudiogramChart';
import { API_BASE_URL } from '../services/httpClient';

/**
 * ============================================================================
 * HEAREN — CLINICAL AUDIOGRAM PHOTO READER
 * ============================================================================
 *
 * Parcours V1 :
 *
 * 1. Prendre une photo ou choisir une photo existante.
 * 2. Envoyer directement l’image au lecteur clinique.
 * 3. L’IA décide si l’image est exploitable.
 * 4. Si nécessaire, l’utilisateur peut recommencer.
 *
 * Metsuke et l’auto-capture restent conservés dans le projet,
 * mais ne participent plus au parcours utilisateur V1.
 * ============================================================================
 */

const ClinicalAudiogramPhotoReader = ({ t }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  /**
   * Envoie une image sélectionnée ou photographiée
   * au lecteur clinique.
   */
  const analyzePhoto = async (photo) => {
    const imageURL = photo?.webPath;

    if (!imageURL) {
      throw new Error(
        'No readable image URL returned by the native photo picker'
      );
    }

    console.log(
      '[ClinicalAudiogramPhotoReader] Reading image:',
      imageURL
    );

    const fileResponse = await fetch(imageURL);

    console.log(
      '[ClinicalAudiogramPhotoReader] Local image response:',
      fileResponse.status,
      fileResponse.ok
    );

    if (!fileResponse.ok) {
      throw new Error(
        `Unable to read selected image: ${fileResponse.status}`
      );
    }

    const blob = await fileResponse.blob();

    const formData = new FormData();

    formData.append(
      'file',
      blob,
      'clinical-audiogram.jpg'
    );

    const backendUrl = API_BASE_URL;

    console.log(
      '[ClinicalAudiogramPhotoReader] Sending image to clinical reader'
    );

    console.log(
      '[ClinicalAudiogramPhotoReader] BACKEND URL:',
      backendUrl
    );

    console.log(
      '[ClinicalAudiogramPhotoReader] FULL ENDPOINT:',
      `${backendUrl}/api/clinical-audiogram/read`
    );

    const response = await fetch(
      `${backendUrl}/api/clinical-audiogram/read`,
      {
        method: 'POST',
        body: formData,
      }
    );

    console.log(
      '[ClinicalAudiogramPhotoReader] Clinical reader HTTP response:',
      response.status,
      response.ok
    );

    if (!response.ok) {
      const errorBody = await response.text();

      throw new Error(
        `Clinical reader failed (${response.status}): ${errorBody}`
      );
    }

    const data = await response.json();

    console.log(
      '[ClinicalAudiogramPhotoReader] ROOT FIELDS:',
      Object.keys(data)
    );

    console.log(
      '[ClinicalAudiogramPhotoReader] NEW FIELDS:',
      {
        status: data.status,
        uncertain_threshold_count: data.uncertain_threshold_count,
        readable_threshold_count: data.readable_threshold_count,
        missing_threshold_count: data.missing_threshold_count,

        reader_status: data.reader?.status,
        reader_uncertain_threshold_count:
          data.reader?.uncertain_threshold_count,
        reader_readable_threshold_count:
          data.reader?.readable_threshold_count,
        reader_missing_threshold_count:
          data.reader?.missing_threshold_count,

        validation: data.reader?.validation,
      }
    );
    setAnalysisResult(data);
  };

  /**
   * Parcours commun à la caméra et à la photothèque.
   */
  const openPhotoSource = async (source) => {
    try {
      setAnalyzing(false);
      setAnalysisResult(null);
      setAnalysisError(null);

      const photo = await Camera.getPhoto({
        source,
        resultType: CameraResultType.Uri,
        quality: 90,
        allowEditing: false,
        correctOrientation: true,
      });

      console.log(
        '[ClinicalAudiogramPhotoReader] Native photo returned:',
        {
          source,
          format: photo.format,
          path: photo.path,
          webPath: photo.webPath,
        }
      );

      setAnalyzing(true);

      await analyzePhoto(photo);

    } catch (error) {
      /*
       * Une fermeture volontaire de la caméra ou de la photothèque
       * ne doit pas être présentée comme une erreur clinique.
       */
      const message =
        error?.message || String(error);

      console.error(
        '[ClinicalAudiogramPhotoReader] Photo import failed:',
        message
      );

      if (
        !message.toLowerCase().includes('cancel') &&
        !message.toLowerCase().includes('user cancelled')
      ) {
        setAnalysisError(message);
      }

    } finally {
      setAnalyzing(false);
    }
  };

  const takePhoto = () => {
    return openPhotoSource(CameraSource.Camera);
  };

  const choosePhoto = () => {
    return openPhotoSource(CameraSource.Photos);
  };

    const clinicalAudiogram =
      analysisResult?.clinical_audiogram ??
      analysisResult?.reader?.clinical_audiogram ??
      analysisResult?.reader?.result?.clinical_audiogram ??
      null;

    const rightEarThresholds =
      clinicalAudiogram?.right_air_conduction ?? {};

    const leftEarThresholds =
      clinicalAudiogram?.left_air_conduction ?? {};

      console.log(
      "[ClinicalAudiogramPhotoReader] Clinical audiogram:",
      clinicalAudiogram
    );

    console.log(
      "[ClinicalAudiogramPhotoReader] Right thresholds:",
      rightEarThresholds
    );

    console.log(
      "[ClinicalAudiogramPhotoReader] Left thresholds:",
      leftEarThresholds
    );

    return (
    <div className="space-y-8">

      <div>
        <h2 className="text-white text-3xl font-medium">
          {t?.clinicalAudiogramImportTitle ||
            'Importer votre audiogramme'}
        </h2>

        <p className="mt-3 text-white/70 text-lg">
          {t?.clinicalAudiogramImportDesc ||
            'Prenez une photo de votre audiogramme ou choisissez une image existante.'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* PRENDRE UNE PHOTO */}
        <button
          type="button"
          onClick={takePhoto}
          disabled={analyzing}
          className="
            rounded-3xl
            border border-white/30
            bg-white/10
            p-7
            text-white
            text-left
            active:scale-[0.98]
            transition-all
            disabled:opacity-50
            disabled:cursor-not-allowed
          "
        >
          <div className="text-3xl mb-4">
            📷
          </div>

          <div className="text-xl font-semibold">
            {t?.clinicalAudiogramTakePhoto ||
              'Prendre une photo'}
          </div>

          <p className="mt-2 text-white/65">
            {t?.clinicalAudiogramTakePhotoDesc ||
              'Photographiez l’ensemble de votre audiogramme.'}
          </p>
        </button>

        {/* CHOISIR UNE PHOTO */}
        <button
          type="button"
          onClick={choosePhoto}
          disabled={analyzing}
          className="
            rounded-3xl
            border border-white/30
            bg-white/10
            p-7
            text-white
            text-left
            active:scale-[0.98]
            transition-all
            disabled:opacity-50
            disabled:cursor-not-allowed
          "
        >
          <div className="text-3xl mb-4">
            🖼️
          </div>

          <div className="text-xl font-semibold">
            {t?.clinicalAudiogramChoosePhoto ||
              'Choisir une photo'}
          </div>

          <p className="mt-2 text-white/65">
            {t?.clinicalAudiogramChoosePhotoDesc ||
              'Sélectionnez une image déjà présente dans votre photothèque.'}
          </p>
        </button>

      </div>

      {/* ANALYSE EN COURS */}
      {analyzing && (
        <div
          className="
            rounded-3xl
            border border-white/20
            bg-white/10
            p-6
            text-white
          "
        >
          <div className="text-lg font-medium">
            {t?.clinicalAudiogramAnalyzing ||
              'Analyse de votre audiogramme en cours…'}
          </div>

          <p className="mt-2 text-white/65">
            {t?.clinicalAudiogramAnalyzingDesc ||
              'Hearen vérifie la qualité de l’image avant de lire les informations disponibles.'}
          </p>
        </div>
      )}

      {/* ERREUR TECHNIQUE PROVISOIRE */}
      {analysisError && !analyzing && (
        <div
          className="
            rounded-3xl
            border border-white/25
            bg-white/10
            p-6
            text-white
          "
        >
          <div className="text-lg font-medium">
            {t?.clinicalAudiogramImportError ||
              'Cette image n’a pas pu être analysée.'}
          </div>

          <p className="mt-2 text-white/65">
            {analysisError}
            
          </p>
        </div>
      )}

      {/* RÉSULTAT DE L’ANALYSE */}
      {analysisResult && !analyzing && (
        <div className="space-y-6">

          {/* RÉSUMÉ */}
          <div
            className="
              rounded-3xl
              border border-white/20
              bg-white/10
              p-6
              text-white
            "
          >
            <div className="text-lg font-medium">
              {analysisResult?.summary?.title ||
                t?.clinicalAudiogramAnalysisComplete ||
                'Analyse terminée'}
            </div>

            {analysisResult?.summary?.message && (
              <p className="mt-2 text-white/70">
                {analysisResult.summary.message}
              </p>
            )}
          </div>

          {/* AUDIOGRAMME RECONSTRUIT */}
          <ClinicalAudiogramChart
            rightEar={rightEarThresholds}
            leftEar={leftEarThresholds}
            title={
              t?.clinicalAudiogramReconstructedTitle ||
              'Audiogramme reconstruit'
            }
          />

          {/* VÉRIFICATION UTILISATEUR */}
          <div
            className="
              rounded-3xl
              border border-white/15
              bg-white/5
              p-5
              text-white
            "
          >
            <div className="font-medium">
              {t?.clinicalAudiogramReviewTitle ||
                'Vérifiez le résultat'}
            </div>

            <p className="mt-2 text-sm text-white/65">
              {t?.clinicalAudiogramReviewDesc ||
                'Ce graphique représente ce que Hearen a compris de votre document. Comparez-le avec l’audiogramme original avant de confirmer les données.'}
            </p>
          </div>

        </div>
      )}

    </div>
  );
};

export default ClinicalAudiogramPhotoReader;